// Sentry boundary for the embedded frontend. Like api/client.ts, this is the
// single module allowed to read Sentry-related `import.meta.env` — components
// and hooks go through the exported helpers, never `Sentry.*` or the env
// directly.
//
// This app runs as a zoid iframe inside the presenter, which has its OWN
// Sentry (Vue) client on the parent window. The iframe is a separate document
// with its own `window`, so this @sentry/react client is fully isolated from
// the host's — no global collision.
//
// No-ops without VITE_SENTRY_DSN: initSentry() returns early, so local dev and
// any build that hasn't been given a DSN simply don't report. Sentry is
// enabled per-env by setting VITE_SENTRY_DSN in that env's .env.* file.
import * as Sentry from "@sentry/react";

// Inlined at build time by Vite. Empty / unset -> Sentry stays off.
const DSN = import.meta.env.VITE_SENTRY_DSN;

export function initSentry(): void {
  if (!DSN) return;

  // Sentry must never break app bootstrap or the first render. initSentry runs
  // before createRoot in main.tsx, so a throw here would blank the iframe.
  // Swallow any init failure and carry on without error reporting.
  try {
    init();
  } catch (err) {
    console.warn("[sentry] init skipped — continuing without error reporting:", err);
  }
}

function init(): void {
  Sentry.init({
    dsn: DSN,
    // staging | canary | production come straight from Vite's build --mode; an
    // explicit VITE_SENTRY_ENVIRONMENT overrides it if ever needed.
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    // FE and BE currently share ONE Sentry project, so tag every frontend event
    // `component=frontend` — the axis to filter/alert on now and the seam to
    // split on if the app later gets its own project (VITE_SENTRY_DSN is
    // already independent from the backend's SENTRY_DSN).
    initialScope: { tags: { component: "frontend" } },
    // Optional build id / git SHA injected by CI. Undefined is fine.
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: false }),
    ],
    // Mirror the presenter's sampling: light traces, no baseline replay,
    // capture a slice of error sessions.
    tracesSampleRate: 0.02,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.2,
    // Drop expected control-flow errors before they reach Sentry: 401s are the
    // re-auth signal (UnauthorizedError) and 4xx ApiErrors are handled by
    // callers. Mirrors the backend beforeSend (packages/backend/src/instrument.ts).
    beforeSend(event, hint) {
      const err = hint?.originalException as
        | { name?: string; status?: number }
        | undefined;
      if (err?.name === "UnauthorizedError") return null;
      if (typeof err?.status === "number" && err.status >= 400 && err.status < 500) {
        return null;
      }
      return event;
    },
  });
}

// Attribute captured events to the current host user. Called from useSentryUser
// once the zoid handshake hydrates identity; passing null clears it. No-ops
// when Sentry is disabled.
export function setSentryUser(user: { id?: string; email?: string } | null): void {
  if (!DSN) return;
  // Called from a React effect — a Sentry failure here must not surface as a
  // render/runtime error. Best-effort, swallow.
  try {
    Sentry.setUser(user?.id ? { id: user.id, email: user.email } : null);
  } catch {
    /* swallow — identity tagging is best-effort */
  }
}
